import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  Input,
  Label,
  Row,
  Col,
  Button,
  FormFeedback,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import { Country, State, City } from "country-state-city";
import { addStudent, editStudent } from "utils/apiService";
import { toast } from "react-toastify";
import { getCountryCallingCodes } from "../../utils/phoneUtils";

interface SelectOption {
  value: string;
  label: string;
}

const StudentModal = ({ isOpen, toggle, student, isEdit, refreshData }) => {
  const [countries, setCountries] = useState<SelectOption[]>([]);
  const [states, setStates] = useState<SelectOption[]>([]);
  const [cities, setCities] = useState<SelectOption[]>([]);

  useEffect(() => {
    const countryList = Country.getAllCountries().map((country) => ({
      value: country.isoCode,
      label: country.name,
    }));
    setCountries(countryList);
  }, []);

  const fetchStates = (countryCode: string | undefined) => {
    if (!countryCode) return;
    const stateList = State.getStatesOfCountry(countryCode).map((state) => ({
      value: state.isoCode,
      label: state.name,
    }));
    setStates(stateList);
    setCities([]);
  };

  const fetchCities = (
    countryCode: string | undefined,
    stateCode: string | undefined
  ) => {
    if (!countryCode || !stateCode) return;
    const cityList = City.getCitiesOfState(countryCode, stateCode).map(
      (city) => ({
        value: city.name,
        label: city.name,
      })
    );
    setCities(cityList);
  };


  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: student?.user_id || "",
      name: student?.user_name || "",
      email: student?.user_email || "",
      password: "",
      confirmPassword: "",
      country: student?.country || "",
      state: student?.state || "",
      city: student?.city || "",
      pinCode: student?.pincode || "",
      address: student?.address || "",
      phone: student?.phone_number || "",
      countryCode: student?.country_code
        ? `+${student.country_code.replace(/\D/g, "")}`
        : "+91",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      email: Yup.string().email("Invalid email").required("Email is required"),
      countryCode: Yup.string().required("Country code is required"),
      phone: Yup.string().required("Phone is required"),
      ...(isEdit
        ? {}
        : {
            password: Yup.string()
              .matches(
                /^(?=.*\d)(?=.*[@$#!%?&])[A-Za-z\d@$#!%?&]{8,}$/,
                "Password must be at least 8 characters long, contain at least one number and one special character."
              )
              .required("Password is required"),
            confirmPassword: Yup.string()
              .oneOf([Yup.ref("password")], "Passwords must match")
              .required("Confirm Password is required"),
          }),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        if (isEdit) {
          const payload = {
            ...values,
            countryCode: values.countryCode.replace(/\D/g, ""),
          };
          await editStudent(payload);
        } else {
          await addStudent(values);
          toast.success("Student Added successfully!");
          resetForm();
        }
        toggle();
        refreshData();
      } catch (err) {
        console.error("Error saving student:", err);
      }
    },
  });

  useEffect(() => {
    const countryISO = countries.find(
      (c) => c.label === formik.values.country
    )?.value;
    if (countryISO) {
      console.log("Fetching states for country ISO:", countryISO);
      fetchStates(countryISO);
    }
  }, [formik.values.country]);

  useEffect(() => {
    const countryISO = countries.find(
      (c) => c.label === formik.values.country
    )?.value;
    const stateISO = states.find((s) => s.label === formik.values.state)?.value;

    if (countryISO && stateISO) {
      console.log("Fetching cities for state ISO:", stateISO);
      fetchCities(countryISO, stateISO);
    }
  }, [formik.values.state, states, countries]);

  return (
    <Modal isOpen={isOpen} toggle={toggle} size={"lg"} centered>
      <ModalHeader toggle={toggle}>
        {isEdit ? "Edit Student" : "Add Student"}
      </ModalHeader>
      <ModalBody>
        <Form onSubmit={formik.handleSubmit}>
          <Row>
            <Col md={6}>
              <div className="mb-3">
                <Label>Name</Label>
                <Input
                  name="name"
                  type="text"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.name}
                  invalid={formik.touched.name && !!formik.errors.name}
                />
                {formik.touched.name && formik.errors.name && (
                  <FormFeedback>{String(formik.errors.name)}</FormFeedback>
                )}
              </div>

              <div className="mb-3">
                <Label>Email</Label>
                <Input
                  name="email"
                  type="email"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.email}
                  invalid={formik.touched.email && !!formik.errors.email}
                />
              </div>

              <Row className="mb-3">
                <Col md={4}>
                  <Label>Country Code</Label>
                  <Select
                    options={getCountryCallingCodes()}
                    onChange={(selected) =>
                      formik.setFieldValue("countryCode", selected?.value)
                    }
                    value={getCountryCallingCodes().find(
                      (c) => c.value === formik.values.countryCode
                    )}
                    styles={{
                      menu: (provided) => ({
                        ...provided,
                        zIndex: 9999,
                        backgroundColor: "#f8f9fa",
                        boxShadow: "0px 2px 10px rgba(0,0,0,0.15)",
                      }),
                    }}
                  />
                </Col>

                <Col md={8}>
                  <Label>Phone</Label>
                  <Input
                    name="phone"
                    type="text"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.phone}
                    invalid={formik.touched.phone && !!formik.errors.phone}
                  />
                  {formik.touched.phone && formik.errors.phone && (
                    <FormFeedback>{String(formik.errors.phone)}</FormFeedback>
                  )}
                </Col>
              </Row>

              <div className="mb-3">
                <Label>Country</Label>
                <Select
                  options={countries}
                  onChange={(selected) => {
                    formik.setFieldValue("country", selected?.label || "");
                    fetchStates(selected?.value);
                  }}
                  value={
                    countries.find((c) => c.label === formik.values.country) ||
                    null
                  }
                  styles={{
                    menu: (provided) => ({
                      ...provided,
                      zIndex: 9999,
                      backgroundColor: "white",
                      maxHeight: "150px",
                      overflowY: "auto",
                    }),
                  }}
                />
              </div>
              <div className="mb-3">
                <Label>City</Label>
                <Select
                  options={cities}
                  onChange={(selected) =>
                    formik.setFieldValue("city", selected?.label || "")
                  }
                  value={
                    cities.find((c) => c.label === formik.values.city) || null
                  }
                  styles={{
                    menu: (provided) => ({
                      ...provided,
                      zIndex: 9999,
                      backgroundColor: "white",
                      maxHeight: "150px",
                      overflowY: "auto",
                    }),
                  }}
                  isDisabled={!formik.values.state}
                />
              </div>
            </Col>

            <Col md={6}>
              {!isEdit && (
                <>
                  <div className="mb-3">
                    <Label>Password</Label>
                    <Input
                      name="password"
                      type="password"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.password}
                    />
                  </div>

                  <div className="mb-3">
                    <Label>Confirm Password</Label>
                    <Input
                      name="confirmPassword"
                      type="password"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.confirmPassword}
                    />
                  </div>
                </>
              )}

              <div className="mb-3">
                <Label>Address</Label>
                <Input
                  name="address"
                  type="text"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.address}
                />
              </div>

              <div className="mb-3">
                <Label>State</Label>
                <Select
                  options={states}
                  onChange={(selected) => {
                    formik.setFieldValue("state", selected?.label || "");
                    fetchCities(
                      countries.find((c) => c.label === formik.values.country)
                        ?.value,
                      selected?.value
                    );
                  }}
                  value={
                    states.find((s) => s.label === formik.values.state) || null
                  }
                  styles={{
                    menu: (provided) => ({
                      ...provided,
                      zIndex: 9999,
                      backgroundColor: "white",
                      maxHeight: "150px",
                      overflowY: "auto",
                    }),
                  }}
                  isDisabled={!formik.values.country}
                />
              </div>

              <div className="mb-3">
                <Label>Pin Code</Label>
                <Input
                  name="pinCode"
                  type="text"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.pinCode}
                />
              </div>
            </Col>
          </Row>

          <div className="text-end">
            <Button type="submit" color="success">
              {isEdit ? "Update" : "Add"}
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default StudentModal;
